import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Search, CheckCircle, Clock, FileText, MapPin, Users, X, User, UserCheck, AlertTriangle } from 'lucide-react';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

const formatNaira = (amount) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);

const plotsLabel = (plots = []) => {
  if (!plots || plots.length === 0) return '--';
  const nums = plots.map((p) => p.plot_number).join(', ');
  const block = plots[0]?.location_block ? ` · ${plots[0].location_block}` : '';
  return `${nums}${block}`;
};

const BOAllocations = () => {
  const { role } = useBOAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [byEstate, setByEstate] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterEstate, setFilterEstate] = useState('');
  const [search, setSearch] = useState('');

  // Process-allocation form state
  const [processing, setProcessing] = useState(null); // the row being processed
  const [form, setForm] = useState({
    allocation_date: new Date().toISOString().split('T')[0],
    allocated_in_person: true,
    representative_name: '',
    representative_phone: '',
    representative_relationship: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Undo confirm
  const [undoConfirm, setUndoConfirm] = useState({ open: false, row: null });
  const formRef = useRef(null);

  useEffect(() => {
    if (processing && formRef.current) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [processing]);

  const fetchAllocations = async () => {
    try {
      const params = {};
      if (filterEstate) params.estate = filterEstate;
      const res = await boAPI.getAllocations(params);
      setRows(res.data.allocations || []);
      setByEstate(res.data.by_estate || {});
    } catch (e) {
      toast.error('Failed to load allocations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAllocations(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterEstate]);

  const estateNames = Object.keys(byEstate).sort();
  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => (r.client_info?.full_name || '').toLowerCase().includes(q) || (r.client_info?.phone || '').includes(search));
  }, [rows, search]);

  const allocated = filtered.filter((r) => r.allocation_status === 'allocated');
  const pending = filtered.filter((r) => r.allocation_status !== 'allocated');
  const eligible = pending.filter((r) => r.eligible_for_allocation);
  const notYetPaid = pending.filter((r) => !r.eligible_for_allocation);
  const isReadOnly = role === 'accountant';

  const openProcess = (row) => {
    setProcessing(row);
    setForm({
      allocation_date: new Date().toISOString().split('T')[0],
      allocated_in_person: true,
      representative_name: '',
      representative_phone: '',
      representative_relationship: '',
      notes: '',
    });
  };

  const submitProcess = async (e) => {
    e.preventDefault();
    if (!form.allocated_in_person) {
      if (!form.representative_name.trim() || !form.representative_phone.trim()) {
        toast.error("Representative's name and phone are required when client did not come in person");
        return;
      }
    }
    setSubmitting(true);
    try {
      await boAPI.processAllocation(processing.sale_id, {
        allocation_date: form.allocation_date,
        allocated_in_person: !!form.allocated_in_person,
        representative_name: form.allocated_in_person ? null : form.representative_name.trim(),
        representative_phone: form.allocated_in_person ? null : form.representative_phone.trim(),
        representative_relationship: form.allocated_in_person ? null : (form.representative_relationship || null),
        notes: form.notes || null,
      });
      toast.success(`Allocation processed for ${processing.client_info?.full_name}`);
      setProcessing(null);
      fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process allocation');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmUndo = async () => {
    if (!undoConfirm.row) return;
    try {
      await boAPI.undoAllocation(undoConfirm.row.sale_id);
      toast.success('Allocation reverted to pending');
      setUndoConfirm({ open: false, row: null });
      fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-allocations-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Allocations</h1>
          <p className="text-neutral-500 mt-1">{allocated.length} allocated, {eligible.length} ready to allocate, {notYetPaid.length} still paying — one allocation per sale</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4 flex items-center gap-3"><Users className="h-5 w-5 text-primary-600" /><div><p className="text-lg font-bold">{rows.length}</p><p className="text-xs text-neutral-500">All sales</p></div></CardContent></Card>
          <Card className="border-green-200 shadow-card"><CardContent className="p-4 flex items-center gap-3"><CheckCircle className="h-5 w-5 text-green-600" /><div><p className="text-lg font-bold text-green-700">{allocated.length}</p><p className="text-xs text-neutral-500">Allocated</p></div></CardContent></Card>
          <Card className="border-amber-200 shadow-card"><CardContent className="p-4 flex items-center gap-3"><Clock className="h-5 w-5 text-amber-600" /><div><p className="text-lg font-bold text-amber-600">{eligible.length}</p><p className="text-xs text-neutral-500">Ready (paid)</p></div></CardContent></Card>
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4 flex items-center gap-3"><MapPin className="h-5 w-5 text-primary-600" /><div><p className="text-lg font-bold">{estateNames.length}</p><p className="text-xs text-neutral-500">Estates</p></div></CardContent></Card>
        </div>

        <div className="flex flex-wrap gap-3">
          <select value={filterEstate} onChange={(e) => setFilterEstate(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-allocation-estate">
            <option value="">All Estates</option>
            {estateNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client name or phone..." className="pl-10 rounded-xl text-sm" data-testid="search-allocation" />
          </div>
        </div>

        {/* Process Allocation Form */}
        {processing && (
          <Card ref={formRef} className="border-primary-200 shadow-card" data-testid="process-allocation-form">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Process Allocation</CardTitle>
                <CardDescription>
                  Allocating <strong>{processing.client_info?.full_name}</strong> for {plotsLabel(processing.plots_info)} ({processing.estate_name})
                </CardDescription>
              </div>
              <button onClick={() => setProcessing(null)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitProcess} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Allocation Date *</Label>
                    <Input type="date" value={form.allocation_date} onChange={(e) => setForm({ ...form, allocation_date: e.target.value })} className="mt-1.5 rounded-xl" data-testid="alloc-form-date" />
                  </div>
                </div>

                <div>
                  <Label>Who came for the allocation? *</Label>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${form.allocated_in_person ? 'border-primary-600 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                      <input type="radio" name="who" checked={form.allocated_in_person} onChange={() => setForm({ ...form, allocated_in_person: true })} className="h-4 w-4" data-testid="alloc-form-in-person" />
                      <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-primary-600" />
                        <div>
                          <p className="font-medium text-sm">Client came in person</p>
                          <p className="text-xs text-neutral-500">{processing.client_info?.full_name}</p>
                        </div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${!form.allocated_in_person ? 'border-primary-600 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                      <input type="radio" name="who" checked={!form.allocated_in_person} onChange={() => setForm({ ...form, allocated_in_person: false })} className="h-4 w-4" data-testid="alloc-form-by-rep" />
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-sm">Represented by someone else</p>
                          <p className="text-xs text-neutral-500">Capture rep details below</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {!form.allocated_in_person && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <div>
                      <Label>Representative's Name *</Label>
                      <Input value={form.representative_name} onChange={(e) => setForm({ ...form, representative_name: e.target.value })} className="mt-1.5 rounded-xl bg-white" data-testid="alloc-form-rep-name" />
                    </div>
                    <div>
                      <Label>Representative's Phone *</Label>
                      <Input value={form.representative_phone} onChange={(e) => setForm({ ...form, representative_phone: e.target.value })} className="mt-1.5 rounded-xl bg-white" data-testid="alloc-form-rep-phone" />
                    </div>
                    <div>
                      <Label>Relationship to Client</Label>
                      <Input value={form.representative_relationship} onChange={(e) => setForm({ ...form, representative_relationship: e.target.value })} placeholder="e.g., Spouse, Sibling, Lawyer (optional)" className="mt-1.5 rounded-xl bg-white" data-testid="alloc-form-rep-relationship" />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Notes (optional)</Label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    placeholder="Any extra details about the allocation"
                    className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm resize-none"
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={() => setProcessing(null)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-green-600 hover:bg-green-700" data-testid="alloc-form-submit">
                    {submitting ? 'Allocating...' : 'Confirm Allocation'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Pending Allocations */}
        <Card className="border-amber-200 shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-amber-500" /> Pending Allocation ({pending.length})</CardTitle>
            <CardDescription>All clients with active sales appear here so they can be searched. Only fully-paid sales can be allocated — clients still paying are clearly marked.</CardDescription>
          </CardHeader>
          <CardContent>
            {pending.length === 0 ? (
              <p className="text-center text-neutral-500 py-6">No pending allocations</p>
            ) : (
              <div className="space-y-2">
                {pending.map((r) => {
                  const eligible = r.eligible_for_allocation;
                  return (
                    <div
                      key={r.sale_id}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border ${eligible ? 'bg-amber-50 border-amber-100' : 'bg-neutral-50 border-neutral-200'}`}
                      data-testid={`pending-row-${r.sale_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-neutral-900">{r.client_info?.full_name || '--'}</p>
                          {eligible ? (
                            <Badge className="bg-emerald-100 text-emerald-800">Paid in full</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" /> Payment not completed
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-neutral-600">{plotsLabel(r.plots_info)} · <strong>{r.estate_name}</strong>{r.plot_count > 1 ? ` (${r.plot_count} plots)` : ''}</p>
                        <p className="text-xs text-neutral-400">
                          {r.client_info?.phone} · {eligible ? `${formatNaira(r.total_amount)} paid in full` : `${formatNaira(r.total_paid)} paid of ${formatNaira(r.total_amount)} — ${formatNaira(r.balance)} outstanding`}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {!isReadOnly && eligible && (
                          <Button size="sm" onClick={() => openProcess(r)} className="rounded-xl text-xs bg-green-600 hover:bg-green-700" data-testid={`process-${r.sale_id}`}>
                            <CheckCircle className="h-3 w-3 mr-1" /> Process Allocation
                          </Button>
                        )}
                        {!isReadOnly && !eligible && (
                          <Button size="sm" disabled className="rounded-xl text-xs bg-neutral-300 cursor-not-allowed" data-testid={`process-disabled-${r.sale_id}`}>
                            <AlertTriangle className="h-3 w-3 mr-1" /> Not Eligible
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => navigate(`/backoffice/allocation/${r.client_id}`)} className="rounded-xl text-xs">
                          <FileText className="h-3 w-3 mr-1" /> Letter
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Allocated */}
        <Card className="border-green-200 shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-600" /> Allocated ({allocated.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {allocated.length === 0 ? (
              <p className="text-center text-neutral-500 py-6">No allocations completed yet</p>
            ) : (
              <div className="space-y-2">
                {allocated.map((r) => (
                  <div key={r.sale_id} className="p-3 rounded-xl bg-green-50 border border-green-100" data-testid={`allocated-row-${r.sale_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-neutral-900">{r.client_info?.full_name || '--'}</p>
                          <Badge className="bg-green-100 text-green-800">Allocated</Badge>
                        </div>
                        <p className="text-xs text-neutral-600 mt-0.5">{plotsLabel(r.plots_info)} · <strong>{r.estate_name}</strong>{r.plot_count > 1 ? ` (${r.plot_count} plots)` : ''}</p>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                          <p className="text-neutral-700"><span className="text-neutral-500">Allocated on:</span> {r.allocation_date || '--'}</p>
                          <p className="text-neutral-700">
                            <span className="text-neutral-500">Received by:</span>{' '}
                            {r.allocated_in_person ? (
                              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> Client in person</span>
                            ) : (
                              <span className="inline-flex items-center gap-1"><UserCheck className="h-3 w-3 text-amber-700" /> {r.representative_name || 'Representative'}{r.representative_relationship ? ` (${r.representative_relationship})` : ''}</span>
                            )}
                          </p>
                          {!r.allocated_in_person && r.representative_phone && (
                            <p className="text-neutral-700"><span className="text-neutral-500">Rep phone:</span> {r.representative_phone}</p>
                          )}
                          {r.allocated_by_name && <p className="text-neutral-700"><span className="text-neutral-500">Processed by:</span> {r.allocated_by_name}</p>}
                          {r.allocation_notes && <p className="text-neutral-700 sm:col-span-2"><span className="text-neutral-500">Notes:</span> {r.allocation_notes}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/backoffice/allocation/${r.client_id}`)} className="rounded-xl text-xs" data-testid={`letter-${r.sale_id}`}>
                          <FileText className="h-3 w-3 mr-1" /> Letter
                        </Button>
                        {!isReadOnly && (
                          <Button size="sm" variant="ghost" onClick={() => setUndoConfirm({ open: true, row: r })} className="rounded-xl text-xs text-red-500 hover:text-red-700" data-testid={`undo-${r.sale_id}`}>
                            Undo
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={undoConfirm.open}
        onOpenChange={(o) => !o && setUndoConfirm({ open: false, row: null })}
        title="Revert this allocation?"
        description={undoConfirm.row ? `${undoConfirm.row.client_info?.full_name}'s allocation for ${plotsLabel(undoConfirm.row.plots_info)} will move back to Pending. Representative details will be cleared.` : ''}
        confirmLabel="Yes, revert"
        destructive={false}
        onConfirm={confirmUndo}
      />
    </BOLayout>
  );
};

export default BOAllocations;
